import React from 'react';
import { Box, IconButton, CircularProgress, Tooltip } from '@mui/material';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined';
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { useQueryClient } from '@tanstack/react-query';
import { useDownloadStore } from '@stores/downloadStore';
import { useOfflineDownloadService } from '@services/offlineDownloadService';

type DownloadCellProps = {
  jobNumber: string;
};

export const DownloadCell: React.FC<DownloadCellProps> = ({ jobNumber }) => {
  const queryClient = useQueryClient();
  const downloadStatus = useDownloadStore(state =>
    state.getDownloadStatus(jobNumber)
  );
  const downloadProgress = useDownloadStore(state =>
    state.getDownloadProgress(jobNumber)
  );
  const { downloadWorkOrder } = useOfflineDownloadService();

  const handleClick = async (event: React.MouseEvent) => {
    event.stopPropagation();

    if (downloadStatus === 'cached' || downloadStatus === 'update_available') {
      const store = useDownloadStore.getState();
      await store.clearDownloadData(jobNumber);
      // Invalidate queries to refresh the UI after clearing download
      await queryClient.invalidateQueries({ queryKey: ['workorders'] });
      await queryClient.invalidateQueries({
        queryKey: ['offline', 'workorders', 'downloaded-only']
      });
      // Also refetch to ensure immediate update
      await queryClient.refetchQueries({
        queryKey: ['offline', 'workorders', 'downloaded-only']
      });
    } else if (downloadStatus === 'not_cached' || downloadStatus === 'error') {
      const success = await downloadWorkOrder(jobNumber);

      if (success) {
        // Force refresh all work order related queries after successful download
        await queryClient.invalidateQueries({ queryKey: ['workorders'] });
        await queryClient.invalidateQueries({
          queryKey: ['offline', 'workorders', 'downloaded-only']
        });
        // Also refetch to ensure immediate update
        await queryClient.refetchQueries({
          queryKey: ['offline', 'workorders', 'downloaded-only']
        });
      }
    }
  };

  const getTooltipTitle = () => {
    switch (downloadStatus) {
      case 'not_cached':
        return 'Download full details for offline use';
      case 'downloading':
        return `Downloading... ${downloadProgress?.progress || 0}%`;
      case 'cached':
        return 'Downloaded - Click to remove';
      case 'update_available':
        return 'Update available - Click to update';
      case 'error':
        return 'Download failed - Click to retry';
      default:
        return '';
    }
  };

  const getIcon = () => {
    switch (downloadStatus) {
      case 'not_cached':
        return <CloudDownloadOutlinedIcon fontSize="small" />;
      case 'downloading':
        return (
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress
              size={20}
              variant="determinate"
              value={downloadProgress?.progress || 0}
            />
          </Box>
        );
      case 'cached':
        return (
          <CloudDoneOutlinedIcon
            fontSize="small"
            sx={{ color: 'success.main' }}
          />
        );
      case 'update_available':
        return (
          <CloudSyncOutlinedIcon
            fontSize="small"
            sx={{ color: 'warning.main' }}
          />
        );
      case 'error':
        return (
          <ErrorOutlineOutlinedIcon
            fontSize="small"
            sx={{ color: 'error.main' }}
          />
        );
      default:
        return <CloudDownloadOutlinedIcon fontSize="small" />;
    }
  };

  return (
    <Tooltip title={getTooltipTitle()}>
      <span>
        <IconButton
          size="small"
          onClick={handleClick}
          disabled={downloadStatus === 'downloading'}
          sx={{ p: 0.5 }}
        >
          {getIcon()}
        </IconButton>
      </span>
    </Tooltip>
  );
};
